import styled from 'styled-components';

/**
 * Verified-moderator shield, shown next to a mod's nickname on their chat
 * messages. Rendered as its own distinct UI chip — NOT text inside the
 * nickname — so a real badge can't be imitated by typing a shield emoji.
 *
 * Trust comes from provenance: the server stamps `message.isMod` from the
 * authenticated socket (room_mod table), never from client input. This
 * component only draws the flag the server already verified.
 *
 * Blue shield; global mods get a deeper blue so the tier still reads.
 * The tooltip is a CSS tooltip (not the native `title` attribute, whose
 * ~3s show-delay made it feel broken) so it appears instantly on hover.
 */
const Shield = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: ${(props) => (props.$global ? '#1d6fd6' : '#3b9bff')};
  opacity: 1;
  cursor: help;
  vertical-align: middle;
  /* Ensure hover works even if an ancestor sets pointer-events: none */
  pointer-events: auto;

  svg {
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none;
  }

  /* CSS tooltip — instant, no native ~3s delay */
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    background: rgba(20, 20, 28, 0.97);
    color: #fff;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 1;
    padding: 5px 8px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.08s ease;
    pointer-events: none;
    z-index: 1000;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

export default function ModBadge({ isGlobalMod = false, className }) {
  const label = isGlobalMod ? 'Verified moderator (global)' : 'Verified moderator';
  return (
    <Shield
      $global={isGlobalMod}
      className={className}
      data-tooltip={label}
      aria-label={label}
      role="img"
    >
      {/* Filled shield with a check — readable at 14px */}
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 1 3 5v6c0 5.25 3.6 10.16 9 11 5.4-.84 9-5.75 9-11V5l-9-4Z" />
        <path
          d="m10.6 15.2-2.8-2.8 1.3-1.3 1.5 1.5 3.9-3.9 1.3 1.3-5.2 5.2Z"
          fill="#fff"
        />
      </svg>
    </Shield>
  );
}
